import type { RouteObject } from "react-router";

import { attendanceRoutes } from "@/app/routes/hr/attendanceRoutes";
import { engagementRoutes } from "@/app/routes/hr/engagementRoutes";
import { lifecycleRoutes } from "@/app/routes/hr/lifecycleRoutes";
import { payrollRoutes } from "@/app/routes/hr/payrollRoutes";
import { performanceRoutes } from "@/app/routes/hr/performanceRoutes";
import { recruitmentRoutes } from "@/app/routes/hr/recruitmentRoutes";
import { timeOffRoutes } from "@/app/routes/hr/timeOffRoutes";
import { RequireCapability } from "@/app/session/RequireCapability";

const AREAS = [
  attendanceRoutes,
  timeOffRoutes,
  payrollRoutes,
  lifecycleRoutes,
  performanceRoutes,
  recruitmentRoutes,
  engagementRoutes,
];

/* The people area (feature "people"): everyone's own HR page, and HR's hub of records. Each
   page answers 404 while the area is switched off, and the hub also without hr.view, as the
   server does. Attendance, time off, payroll and the lifecycle add their pages in
   `routes/hr/*` (see AreaRoutes). */
export const hrRoutes: RouteObject[] = [
  {
    element: <RequireCapability feature="people" />,
    children: [
      {
        path: "me/hr",
        lazy: async () => ({
          Component: (await import("@/features/people/routes/MyHrLayout")).default,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import("@/features/people/routes/MyHrPage")).default,
            }),
          },
          ...AREAS.flatMap((area) => area.me),
        ],
      },
      ...AREAS.flatMap((area) => area.top),
      {
        element: <RequireCapability permission="hr.view" />,
        children: [
          {
            path: "people",
            lazy: async () => ({
              Component: (await import("@/features/people/routes/PeopleLayout")).default,
            }),
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import("@/features/people/routes/DirectoryTab")).default,
                }),
              },
              {
                path: "org-chart",
                lazy: async () => ({
                  Component: (await import("@/features/people/routes/OrgChartTab")).default,
                }),
              },
              {
                path: "settings",
                lazy: async () => ({
                  Component: (await import("@/features/people/routes/PeopleSettingsTab")).default,
                }),
              },
              ...AREAS.flatMap((area) => area.people),
            ],
          },
          {
            path: "people/:userId",
            lazy: async () => ({
              Component: (await import("@/features/people/routes/EmployeeLayout")).default,
            }),
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import("@/features/people/routes/EmployeeOverviewTab")).default,
                }),
              },
              {
                path: "timeline",
                lazy: async () => ({
                  Component: (await import("@/features/people/routes/EmployeeTimelineTab")).default,
                }),
              },
              {
                path: "documents",
                lazy: async () => ({
                  Component: (await import("@/features/people/routes/EmployeeDocumentsTab"))
                    .default,
                }),
              },
              {
                path: "certifications",
                lazy: async () => ({
                  Component: (await import("@/features/people/routes/EmployeeCertificationsTab"))
                    .default,
                }),
              },
              ...AREAS.flatMap((area) => area.employee),
            ],
          },
        ],
      },
    ],
  },
];
